// SCR-STAFF-305 — صف جابه‌جایی (US-SCH-036؛ BR-SCH-035؛ WF-10): نوبت‌های NeedsReschedule با علت، موبایل و وضعیت پیگیری، پیشنهاد زمان
// جایگزین و جابه‌جایی مستقیم، ارسال گروهی پیام «انتخاب زمان جدید» (MSG) و تأیید مطالعه ردیف‌های اطلاعی (فوت — US-PAT-064).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type FollowUpStatus, type RescheduleQueueItemView, type SlotView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, ListPage, LoadingState, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { appointmentPath, formatDay, formatTime, schKeys, useBookingOptions, useActiveBranch } from './common';

export const SCR_STAFF_305 = 'SCR-STAFF-305';

const FOLLOW_UPS: readonly FollowUpStatus[] = ['Pending', 'Contacted', 'NoAnswer'];

/** پیشنهاد زمان جایگزین و جابه‌جایی نوبت ردیف (AC-SCH-036.1). */
function SuggestionsDialog({ item, onClose, onMoved }: { item: RescheduleQueueItemView | null; onClose: () => void; onMoved: () => void }) {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const titleId = useId();
  const { branchId } = useActiveBranch();
  const timeZone = useBookingOptions(branchId, null, item !== null).data?.timeZone;
  const suggestions = useQuery({
    queryKey: schKeys.suggestions(session?.tenantId, item?.id ?? ''),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/scheduling/reschedule-queue/{id}/suggestions', { params: { path: { id: item?.id ?? '' } } })),
    enabled: item !== null,
  });
  const move = useMutation({
    mutationFn: (slot: SlotView) =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/appointments/{id}:reschedule', {
          params: { path: { id: item?.appointmentId ?? '' } },
          body: {
            start: slot.start,
            providerId: slot.providerId,
            roomId: slot.roomId ?? null,
            rowVersion: null,
            scope: null,
            reason: 'reschedule_queue',
            overrideAdmissionRules: false,
            overrideLock: false,
          },
        }),
      ),
    onSuccess: onMoved,
  });
  return (
    <Dialog open={item !== null} onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>
        {t('sch:queue.suggestions_title')}
        {item?.patientName ? ` · ${item.patientName}` : ''}
      </DialogTitle>
      <DialogContent>
        {suggestions.isPending ? (
          <LoadingState rows={3} />
        ) : suggestions.error ? (
          <ErrorState error={suggestions.error} compact />
        ) : (suggestions.data?.slots ?? []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('sch:queue.no_suggestions')}
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }} data-testid="queue-suggestions">
            {(suggestions.data?.slots ?? []).map((slot) => (
              <Button
                key={`${slot.providerId}|${slot.start}`}
                variant="outlined"
                disabled={move.isPending}
                onClick={() => move.mutate(slot)}
                data-testid="queue-suggestion"
                data-start={slot.start}
              >
                {formatDay(slot.date, 'EEEE d MMMM')} · {formatTime(slot.start, timeZone)} · {slot.providerName}
              </Button>
            ))}
          </Stack>
        )}
        {move.error ? <ErrorState error={move.error} compact /> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function RescheduleQueuePage() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const { branchId } = useActiveBranch();
  const timeZone = useBookingOptions(branchId).data?.timeZone;
  const [includeClosed, setIncludeClosed] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [suggesting, setSuggesting] = useState<RescheduleQueueItemView | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const tenantId = session?.tenantId;
  const queue = useQuery({
    queryKey: schKeys.queue(tenantId, includeClosed),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/scheduling/reschedule-queue', { params: { query: { includeClosed, limit: 100 } } })),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: schKeys.all(tenantId) });
  const followUp = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FollowUpStatus }) =>
      unwrap(tenantApi.POST('/api/v1/scheduling/reschedule-queue/{id}:follow-up', { params: { path: { id } }, body: { status, note: null } })),
    onSuccess: refresh,
  });
  const contact = useMutation({
    mutationFn: (itemIds: string[]) => unwrap(tenantApi.POST('/api/v1/scheduling/reschedule-queue:contact', { body: { itemIds } })),
    onSuccess: (result, itemIds) => {
      setSelected([]);
      setNotice(t('sch:queue.contact_sent', { count: faDigits(Number(result.requested ?? itemIds.length)) }));
      refresh();
    },
  });
  const acknowledge = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.POST('/api/v1/scheduling/reschedule-queue/{id}:acknowledge', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const restore = useMutation({
    mutationFn: (ids: string[]) =>
      ids.length === 1
        ? unwrap(tenantApi.POST('/api/v1/scheduling/reschedule-queue/{id}:restore', { params: { path: { id: ids[0] ?? '' }, header: { 'Idempotency-Key': globalThis.crypto.randomUUID() } } })).then(() => ({ restored: 1 }))
        : unwrap(tenantApi.POST('/api/v1/scheduling/reschedule-queue:restore', { params: { header: { 'Idempotency-Key': globalThis.crypto.randomUUID() } }, body: { ids } })),
    onSuccess: (result) => {
      setSelected([]);
      setNotice(t('sch:queue.restored_count', { count: faDigits(Number(result.restored)) }));
      refresh();
    },
  });
  const full = access.accessMode === 'Full';
  const items = queue.data?.items ?? [];
  const contactable = items.filter((item) => !item.informational && item.followUp !== 'Rescheduled' && item.followUp !== 'Cancelled');
  const restorableSelected = selected.filter((id) => items.some((item) => item.id === id && item.restorable));
  const toggle = (id: string) => setSelected((current) => (current.includes(id) ? current.filter((value) => value !== id) : [...current, id]));

  const columns: DataTableColumn<RescheduleQueueItemView>[] = [
    {
      id: 'select',
      headerKey: 'sch:queue.columns.select',
      cell: (row) =>
        !row.informational && full ? (
          <Checkbox
            size="small"
            checked={selected.includes(row.id)}
            onChange={() => toggle(row.id)}
            inputProps={{ 'aria-label': t('sch:queue.select_row', { name: row.patientName ?? '' }) }}
          />
        ) : null,
    },
    {
      id: 'time',
      headerKey: 'sch:queue.columns.time',
      cell: (row) => (
        <Link component={RouterLink} to={appointmentPath(row.appointmentId)} data-testid="queue-appointment">
          {formatDay(row.start.slice(0, 10), 'EEEE d MMMM')} · {formatTime(row.start, timeZone)}
        </Link>
      ),
    },
    { id: 'patient', headerKey: 'sch:queue.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'mobile', headerKey: 'sch:queue.columns.mobile', cell: (row) => (row.patientMobile ? <span dir="ltr">{faDigits(row.patientMobile)}</span> : '—') },
    { id: 'provider', headerKey: 'sch:queue.columns.provider', cell: (row) => row.providerName ?? '—' },
    { id: 'service', headerKey: 'sch:queue.columns.service', cell: (row) => (row.serviceMasked ? t('sch:day.masked_service') : (row.serviceName ?? '—')) },
    {
      id: 'cause',
      headerKey: 'sch:queue.columns.cause',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{t(`sch:cause.${row.cause}`)}</span>
          {row.restorable ? <Chip size="small" color="success" label={t('sch:queue.restorable')} data-testid="queue-restorable" /> : null}
        </Stack>
      ),
    },
    {
      id: 'followUp',
      headerKey: 'sch:queue.columns.follow_up',
      cell: (row) =>
        row.informational ? (
          <Typography variant="body2" color="text.secondary">
            {t('sch:queue.informational')}
          </Typography>
        ) : full && FOLLOW_UPS.includes(row.followUp) ? (
          <TextField
            select
            size="small"
            value={row.followUp}
            onChange={(event) => followUp.mutate({ id: row.id, status: event.target.value as FollowUpStatus })}
            inputProps={{ 'aria-label': t('sch:queue.columns.follow_up'), 'data-testid': 'queue-follow-up' }}
          >
            {FOLLOW_UPS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`sch:follow_up.${value}`)}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          t(`sch:follow_up.${row.followUp}`)
        ),
    },
    {
      id: 'actions',
      headerKey: 'sch:queue.columns.actions',
      cell: (row) =>
        !full ? null : row.informational ? (
          <Button size="small" onClick={() => acknowledge.mutate(row.id)} data-testid="queue-acknowledge">
            {t('sch:queue.acknowledge')}
          </Button>
        ) : FOLLOW_UPS.includes(row.followUp) ? (
          <Stack direction="row" spacing={1}>
            {row.restorable ? (
              <Button size="small" disabled={restore.isPending} onClick={() => restore.mutate([row.id])} data-testid="queue-restore">
                {t('sch:queue.restore')}
              </Button>
            ) : null}
            <Button size="small" onClick={() => setSuggesting(row)} data-testid="queue-suggest">
              {t('sch:queue.suggest')}
            </Button>
          </Stack>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_305}
      titleKey="sch:queue.title"
      subtitleKey="sch:queue.subtitle"
      actions={
        full ? (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              disabled={restorableSelected.length === 0 || restore.isPending}
              onClick={() => restore.mutate(restorableSelected)}
              data-testid="queue-restore-group"
            >
              {t('sch:queue.restore_group', { count: faDigits(restorableSelected.length) })}
            </Button>
            <Button
              variant="outlined"
              disabled={selected.length === 0 || contact.isPending}
              onClick={() => contact.mutate(selected)}
              data-testid="queue-contact"
            >
              {t('sch:queue.contact', { count: faDigits(selected.length) })}
            </Button>
          </Stack>
        ) : undefined
      }
      filters={
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={<Checkbox checked={includeClosed} onChange={(event) => setIncludeClosed(event.target.checked)} />}
            label={t('sch:queue.include_closed')}
          />
          {full && contactable.length > 0 ? (
            <Button size="small" onClick={() => setSelected(contactable.map((item) => item.id))}>
              {t('sch:queue.select_all')}
            </Button>
          ) : null}
        </Stack>
      }
    >
      {notice ? (
        <Alert severity="success" onClose={() => setNotice(null)} data-testid="queue-notice">
          {notice}
        </Alert>
      ) : null}
      <DataTable
        captionKey="sch:queue.title"
        columns={columns}
        rows={items}
        getRowId={(row) => row.id}
        isLoading={queue.isPending}
        error={queue.error ?? followUp.error ?? contact.error ?? acknowledge.error ?? restore.error}
        onRetry={() => void queue.refetch()}
        emptyTitleKey="sch:queue.empty"
      />
      <SuggestionsDialog
        item={suggesting}
        onClose={() => setSuggesting(null)}
        onMoved={() => {
          setSuggesting(null);
          setNotice(t('sch:queue.moved'));
          refresh();
        }}
      />
    </ListPage>
  );
}
